/**
 * Vid2MP3 - Home View Controller Module (TypeScript)
 */

import { ExtractorEngine, ExtractedAudioResult } from '../extractor-engine';

export class HomeView {

  private static videoFile: File | null = null;
  private static videoUrl: string | null = null;
  private static videoDuration = 0;
  private static videoSizeBytes = 0;
  private static isConverting = false;
  private static convertedAudio: ExtractedAudioResult | null = null;

  private static outputFormat = 'MP3';
  private static bitrateValue = '192kbps';
  private static bitrateKbps = 192;
  private static useTimeRange = false;
  private static startTime = 0;
  private static endTime = 0;

  // DOM Elements
  private static mainConverterCard: HTMLElement;
  private static initialWorkspaceWrap: HTMLElement;
  private static expandedWorkspaceWrap: HTMLElement;

  private static tabUpload: HTMLButtonElement;
  private static tabUrl: HTMLButtonElement;
  private static dropzoneBox: HTMLElement;
  private static btnChooseFileInner: HTMLButtonElement;
  private static urlBox: HTMLElement;
  private static videoUrlInput: HTMLInputElement;
  private static loadUrlBtn: HTMLButtonElement;
  private static videoFileInput: HTMLInputElement;
  private static videoPlayer: HTMLVideoElement;

  // Options View Elements
  private static formatCards: NodeListOf<HTMLElement>;
  private static bitratePills: NodeListOf<HTMLElement>;
  private static timeRangeToggle: HTMLInputElement;
  private static rangeSliderGroup: HTMLElement;
  private static rangeValText: HTMLElement;
  private static startTimeSlider: HTMLInputElement;
  private static endTimeSlider: HTMLInputElement;

  private static fileInfoName: HTMLElement;
  private static fileInfoMeta: HTMLElement;
  private static fileInfoCloseBtn: HTMLButtonElement;
  private static estOutputVal: HTMLElement;
  private static estOutputSub: HTMLElement;

  private static extractBtn: HTMLButtonElement;
  private static extractBtnText: HTMLElement;

  // Results Elements
  private static resultsContent: HTMLElement;
  private static progressBarFill: HTMLElement;
  private static progressText: HTMLElement;
  private static audioResultPlayer: HTMLAudioElement;
  private static downloadAudioBtn: HTMLAnchorElement;
  private static clearAudioBtn: HTMLButtonElement;

  public static init(): void {
    (window as any).__TS_HOMEVIEW_INIT__ = true;
    this.cacheDom();
    this.bindEvents();
    this.bindFaqAccordion();
  }

  private static cacheDom(): void {
    this.mainConverterCard = document.getElementById('main-converter-card') as HTMLElement;
    this.initialWorkspaceWrap = document.getElementById('initial-workspace-wrap') as HTMLElement;
    this.expandedWorkspaceWrap = document.getElementById('expanded-workspace-wrap') as HTMLElement;

    this.tabUpload = document.getElementById('tab-upload') as HTMLButtonElement;
    this.tabUrl = document.getElementById('tab-url') as HTMLButtonElement;
    this.dropzoneBox = document.getElementById('dropzone-box') as HTMLElement;
    this.btnChooseFileInner = document.getElementById('btn-choose-file-inner') as HTMLButtonElement;
    this.urlBox = document.getElementById('url-box') as HTMLElement;
    this.videoUrlInput = document.getElementById('video-url-input') as HTMLInputElement;
    this.loadUrlBtn = document.getElementById('load-url-btn') as HTMLButtonElement;
    this.videoFileInput = document.getElementById('video-file-input') as HTMLInputElement;
    this.videoPlayer = document.getElementById('video-player') as HTMLVideoElement;

    this.formatCards = document.querySelectorAll('.format-card');
    this.bitratePills = document.querySelectorAll('.quality-pill');

    this.timeRangeToggle = document.getElementById('time-range-toggle') as HTMLInputElement;
    this.rangeSliderGroup = document.getElementById('range-slider-group') as HTMLElement;
    this.rangeValText = document.getElementById('range-val-text') as HTMLElement;
    this.startTimeSlider = document.getElementById('start-time-slider') as HTMLInputElement;
    this.endTimeSlider = document.getElementById('end-time-slider') as HTMLInputElement;

    this.fileInfoName = document.getElementById('file-info-name') as HTMLElement;
    this.fileInfoMeta = document.getElementById('file-info-meta') as HTMLElement;
    this.fileInfoCloseBtn = document.getElementById('file-info-close-btn') as HTMLButtonElement;

    this.estOutputVal = document.getElementById('est-output-val') as HTMLElement;
    this.estOutputSub = document.getElementById('est-output-sub') as HTMLElement;

    this.extractBtn = document.getElementById('extract-btn') as HTMLButtonElement;
    this.extractBtnText = document.getElementById('extract-btn-text') as HTMLElement;

    this.resultsContent = document.getElementById('results-content') as HTMLElement;
    this.progressBarFill = document.getElementById('progress-bar-fill') as HTMLElement;
    this.progressText = document.getElementById('progress-text') as HTMLElement;
    this.audioResultPlayer = document.getElementById('audio-result-player') as HTMLAudioElement;
    this.downloadAudioBtn = document.getElementById('download-audio-btn') as HTMLAnchorElement;
    this.clearAudioBtn = document.getElementById('clear-audio-btn') as HTMLButtonElement;
  }

  private static formatTime(seconds: number): string {
    const mins = Math.floor(seconds / 60);
    const secs = Math.floor(seconds % 60);
    const hh = Math.floor(mins / 60);
    const mm = mins % 60;
    const pad = (n: number) => String(n).padStart(2, '0');
    if (hh > 0) {
      return `${pad(hh)}:${pad(mm)}:${pad(secs)}`;
    }
    return `${pad(mm)}:${pad(secs)}`;
  }

  private static formatBytes(bytes: number): string {
    if (!bytes || bytes <= 0) return '0.0 MB';
    const mb = bytes / (1024 * 1024);
    return `${mb.toFixed(1)} MB`;
  }

  private static updateEstimatedOutputSize(): void {
    let dur = this.videoDuration;
    if (this.useTimeRange && this.endTime > this.startTime) {
      dur = this.endTime - this.startTime;
    }
    dur = Math.max(0.5, dur);

    let approxMB = (this.bitrateKbps * dur) / 8192;
    if (this.outputFormat === 'WAV') {
      approxMB = (1411 * dur) / 8192;
    }

    if (this.estOutputVal) this.estOutputVal.textContent = `~${approxMB.toFixed(1)} MB`;
    if (this.estOutputSub) this.estOutputSub.textContent = `Approximate size based on ${this.outputFormat} at ${this.bitrateValue}.`;
  }

  private static bindEvents(): void {
    if (!this.dropzoneBox) return;

    this.tabUpload.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUpload.classList.add('active');
      this.tabUrl.classList.remove('active');
      this.dropzoneBox.style.display = 'block';
      this.urlBox.style.display = 'none';
    });

    this.tabUrl.addEventListener('click', (e) => {
      e.preventDefault();
      this.tabUrl.classList.add('active');
      this.tabUpload.classList.remove('active');
      this.urlBox.style.display = 'block';
      this.dropzoneBox.style.display = 'none';
    });

    this.dropzoneBox.addEventListener('click', () => this.videoFileInput.click());
    if (this.btnChooseFileInner) {
      this.btnChooseFileInner.addEventListener('click', (e) => {
        e.stopPropagation();
        this.videoFileInput.click();
      });
    }

    this.dropzoneBox.addEventListener('dragover', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.add('dragover');
    });
    this.dropzoneBox.addEventListener('dragleave', () => this.dropzoneBox.classList.remove('dragover'));
    this.dropzoneBox.addEventListener('drop', (e) => {
      e.preventDefault();
      this.dropzoneBox.classList.remove('dragover');
      if (e.dataTransfer?.files && e.dataTransfer.files[0]) {
        this.handleFileSelection(e.dataTransfer.files[0]);
      }
    });

    this.videoFileInput.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      if (target.files && target.files[0]) {
        this.handleFileSelection(target.files[0]);
      }
    });

    this.loadUrlBtn.addEventListener('click', () => {
      const url = this.videoUrlInput.value.trim();
      if (!url) {
        alert('Please enter a valid video URL.');
        return;
      }
      this.handleUrlLoad(url);
    });

    if (this.fileInfoCloseBtn) {
      this.fileInfoCloseBtn.addEventListener('click', () => this.resetToInitialWorkspace());
    }

    this.formatCards.forEach(card => {
      card.addEventListener('click', () => {
        this.formatCards.forEach(c => c.classList.remove('active'));
        card.classList.add('active');
        this.outputFormat = card.getAttribute('data-fmt') || 'MP3';
        if (this.extractBtnText) this.extractBtnText.textContent = `Convert to ${this.outputFormat}`;
        this.updateEstimatedOutputSize();
      });
    });

    this.bitratePills.forEach(pill => {
      pill.addEventListener('click', () => {
        this.bitratePills.forEach(p => p.classList.remove('active'));
        pill.classList.add('active');
        const num = parseInt(pill.getAttribute('data-bitrate') || '192', 10);
        this.bitrateKbps = num;
        this.bitrateValue = `${num} kbps`;
        this.updateEstimatedOutputSize();
      });
    });

    this.timeRangeToggle.addEventListener('change', (e) => {
      const target = e.target as HTMLInputElement;
      this.useTimeRange = target.checked;
      this.rangeSliderGroup.style.display = this.useTimeRange ? 'block' : 'none';
      this.updateEstimatedOutputSize();
    });

    this.startTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (sVal >= eVal) {
        sVal = Math.max(0, eVal - 0.1);
        this.startTimeSlider.value = sVal.toString();
      }
      this.startTime = sVal;
      this.updateRangeDisplay();
      this.updateEstimatedOutputSize();
    });

    this.endTimeSlider.addEventListener('input', () => {
      let sVal = parseFloat(this.startTimeSlider.value);
      let eVal = parseFloat(this.endTimeSlider.value);
      if (eVal <= sVal) {
        eVal = Math.min(this.videoDuration, sVal + 0.1);
        this.endTimeSlider.value = eVal.toString();
      }
      this.endTime = eVal;
      this.updateRangeDisplay();
      this.updateEstimatedOutputSize();
    });

    this.extractBtn.addEventListener('click', () => this.startConversion());
    if (this.clearAudioBtn) this.clearAudioBtn.addEventListener('click', () => this.clearOutputState());
  }

  private static handleFileSelection(file: File): void {
    if (!file.type.startsWith('video/') && !/\.(mp4|mov|avi|webm|mkv)$/i.test(file.name)) {
      alert('Please select a valid video file.');
      return;
    }

    this.videoFile = file;
    this.videoSizeBytes = file.size || 0;
    if (this.videoUrl && this.videoUrl.startsWith('blob:')) URL.revokeObjectURL(this.videoUrl);
    this.videoUrl = URL.createObjectURL(file);

    this.videoPlayer.src = this.videoUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      if (this.fileInfoName) this.fileInfoName.textContent = file.name;
      if (this.fileInfoMeta) this.fileInfoMeta.textContent = `${this.formatBytes(this.videoSizeBytes)} • ${this.formatTime(this.videoDuration)}`;

      this.updateEstimatedOutputSize();
      this.expandToOptionsWorkspace();
    };
  }

  private static extractYouTubeId(url: string): string | null {
    const regExp = /^.*(youtu.be\/|v\/|u\/\w\/|embed\/|watch\?v=|\&v=)([^#\&\?]*).*/;
    const match = url.match(regExp);
    return (match && match[2].length === 11) ? match[2] : null;
  }

  private static async handleUrlLoad(inputUrl: string): Promise<void> {
    let targetUrl = inputUrl.trim().replace(/^http:\/\//i, 'https://');
    if (!targetUrl.startsWith('https://')) {
      targetUrl = 'https://' + targetUrl;
    }

    this.loadUrlBtn.disabled = true;
    this.loadUrlBtn.textContent = 'Resolving...';

    try {
      if (/tiktok\.com/i.test(targetUrl)) {
        const response = await fetch(`https://www.tikwm.com/api/?url=${encodeURIComponent(targetUrl)}`);
        const json = await response.json();
        if (json.code === 0 && json.data && json.data.play) {
          targetUrl = json.data.play.startsWith('http') ? json.data.play : `https://www.tikwm.com${json.data.play}`;
        } else {
          throw new Error(json.msg || 'Unable to parse TikTok video link');
        }
      } else if (/youtube\.com|youtu\.be/i.test(targetUrl)) {
        const ytId = this.extractYouTubeId(targetUrl);
        let resolved = false;

        if (ytId) {
          const invidiousInstances = [
            'https://inv.tux.pizza',
            'https://invidious.nerdvpn.de',
            'https://invidious.flokinet.to',
            'https://invidious.privacydev.net'
          ];
          for (const instance of invidiousInstances) {
            try {
              const res = await fetch(`${instance}/api/v1/videos/${ytId}`);
              if (!res.ok) continue;
              const data = await res.json();
              const mp4Format = data.formatStreams?.find((f: any) => f.container === 'mp4' || f.encoding === 'h264');
              if (mp4Format && mp4Format.url) {
                targetUrl = mp4Format.url;
                resolved = true;
                break;
              }
            } catch {}
          }
        }

        if (!resolved) {
          try {
            const res = await fetch('https://api.cobalt.tools/', {
              method: 'POST',
              headers: { 'Accept': 'application/json', 'Content-Type': 'application/json' },
              body: JSON.stringify({ url: targetUrl })
            });
            const data = await res.json();
            if (data.url) {
              targetUrl = data.url;
              resolved = true;
            }
          } catch {}
        }

        if (!resolved) {
          throw new Error('Unable to extract stream from YouTube link. Please ensure link is public.');
        }
      }
    } catch (err: any) {
      alert(`Error loading video URL: ${err.message || 'Invalid or unsupported link'}`);
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      return;
    }

    this.videoFile = null;
    this.videoSizeBytes = 0;
    this.videoUrl = targetUrl;

    this.videoPlayer.crossOrigin = 'anonymous';
    this.videoPlayer.src = targetUrl;
    this.videoPlayer.load();

    this.videoPlayer.onloadedmetadata = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      this.videoDuration = this.videoPlayer.duration || 10;
      this.startTime = 0;
      this.endTime = this.videoDuration;

      this.startTimeSlider.max = this.videoDuration.toString();
      this.endTimeSlider.max = this.videoDuration.toString();
      this.startTimeSlider.value = '0';
      this.endTimeSlider.value = this.videoDuration.toString();
      this.updateRangeDisplay();

      if (this.fileInfoName) this.fileInfoName.textContent = 'Video Stream from URL';
      if (this.fileInfoMeta) this.fileInfoMeta.textContent = `Online Stream • ${this.formatTime(this.videoDuration)}`;

      this.updateEstimatedOutputSize();
      this.expandToOptionsWorkspace();
    };

    this.videoPlayer.onerror = () => {
      this.loadUrlBtn.disabled = false;
      this.loadUrlBtn.textContent = 'Load';
      alert('Failed to load video stream from URL. Please ensure link is accessible.');
    };
  }

  private static expandToOptionsWorkspace(): void {
    this.mainConverterCard.classList.add('expanded');
    this.initialWorkspaceWrap.style.display = 'none';
    this.expandedWorkspaceWrap.style.display = 'block';
  }

  private static resetToInitialWorkspace(): void {
    if (this.videoUrl && this.videoUrl.startsWith('blob:')) URL.revokeObjectURL(this.videoUrl);
    this.videoFile = null;
    this.videoUrl = null;
    this.videoDuration = 0;
    this.videoSizeBytes = 0;

    this.clearOutputState();

    this.mainConverterCard.classList.remove('expanded');
    this.expandedWorkspaceWrap.style.display = 'none';
    this.initialWorkspaceWrap.style.display = 'block';
    this.videoFileInput.value = '';
  }

  private static updateRangeDisplay(): void {
    if (this.rangeValText) this.rangeValText.textContent = `${this.startTime.toFixed(1)}s - ${this.endTime.toFixed(1)}s`;
  }

  private static async startConversion(): Promise<void> {
    if (!this.videoFile && !this.videoUrl) return;
    if (this.isConverting) return;

    this.isConverting = true;
    this.extractBtn.disabled = true;
    this.extractBtnText.textContent = 'Converting...';

    this.resultsContent.style.display = 'block';
    this.progressBarFill.style.width = '0%';
    this.progressText.textContent = '0%';

    try {
      this.convertedAudio = await ExtractorEngine.extractAudio({
        videoFile: this.videoFile,
        videoUrl: this.videoUrl,
        outputFormat: this.outputFormat,
        useTimeRange: this.useTimeRange,
        startTime: this.startTime,
        endTime: this.endTime,
        onProgress: (percent) => {
          this.progressBarFill.style.width = `${percent}%`;
          this.progressText.textContent = `${percent}%`;
        }
      });

      if (this.convertedAudio) {
        this.audioResultPlayer.src = this.convertedAudio.url;
        this.downloadAudioBtn.href = this.convertedAudio.url;
        this.downloadAudioBtn.download = this.convertedAudio.filename;
        this.downloadAudioBtn.innerHTML = `
          <svg width="14" height="14" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2">
            <path d="M21 15v4a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2v-4"></path>
            <polyline points="7 10 12 15 17 10"></polyline>
            <line x1="12" y1="15" x2="12" y2="3"></line>
          </svg>
          Download ${this.outputFormat}
        `;
      }
    } catch (err: any) {
      alert(`Conversion error: ${err.message || 'Failed to process audio'}`);
    }

    this.isConverting = false;
    this.extractBtn.disabled = false;
    this.extractBtnText.textContent = `Convert to ${this.outputFormat}`;
  }

  private static clearOutputState(): void {
    if (this.convertedAudio && this.convertedAudio.url) {
      URL.revokeObjectURL(this.convertedAudio.url);
    }
    this.convertedAudio = null;
    if (this.resultsContent) this.resultsContent.style.display = 'none';
    if (this.audioResultPlayer) this.audioResultPlayer.src = '';
  }

  private static bindFaqAccordion(): void {
    const faqItems = document.querySelectorAll('.faq-item');
    faqItems.forEach(item => {
      const q = item.querySelector('.faq-question');
      if (q) {
        q.addEventListener('click', () => item.classList.toggle('active'));
      }
    });
  }
}
